/**
 * DirectedVideo: a carousel staged as a directed video (see DirectedSpec).
 *
 * Scenes put one slide — or several stacked into one canvas the camera
 * travels — on stage; elements reveal, get emphasized and leave on the spec's
 * timeline. The slide design itself is never changed: the spec only decides
 * when and how the carousel's own elements appear.
 *
 * Kept in step with server/src/remotion/DirectedVideo.tsx so the editor
 * preview plays exactly what publishes.
 */
import React from 'react';
import { AbsoluteFill, Audio, Easing, Img, interpolate, useCurrentFrame, useVideoConfig } from 'remotion';
import { TransitionSeries, linearTiming, springTiming } from '@remotion/transitions';
import { fade } from '@remotion/transitions/fade';
import { slide } from '@remotion/transitions/slide';
import { wipe } from '@remotion/transitions/wipe';
import { AnyOverlay, TextOverlay, TextRun, TextRunStyle, getRenderableRuns } from '../../models/Post';
import {
  DirectedEase,
  DirectedElementRef,
  DirectedScene,
  DirectedSceneElement,
  DirectedSceneTransition,
  DirectedSpec,
} from '../../models/ChoreographySpec';
import { RemotionSlide } from './RemotionSlide';
import { adaptVideoOverlay } from './adaptVideoOverlay';
import { AnimatedOverlayWrapper, SlideData } from './CarouselVideo';

export interface DirectedVideoProps {
  [key: string]: unknown;
  slides: SlideData[];
  spec: DirectedSpec;
  width: number;
  height: number;
  musicUrl?: string;
  musicVolume?: number;
}

const EASES: Record<DirectedEase, (t: number) => number> = {
  linear: Easing.linear,
  out: Easing.out(Easing.cubic),
  in: Easing.in(Easing.cubic),
  inOut: Easing.inOut(Easing.cubic),
  back: Easing.out(Easing.back(1.6)),
  snap: Easing.bezier(0.2, 0.9, 0.1, 1),
};
const clamp01 = (v: number) => Math.max(0, Math.min(1, v));
const refKey = (r: DirectedElementRef) => `${r.slide}:${r.overlay}`;

function sampleKeys<T extends { t: number; ease?: DirectedEase }>(keys: T[] | undefined, t: number, prop: keyof T, fallback: number): number {
  const ks = (keys ?? []).filter((k) => typeof k[prop] === 'number').sort((a, b) => a.t - b.t);
  if (!ks.length) return fallback;
  const value = (k: T) => k[prop] as unknown as number;
  if (t <= ks[0].t) return value(ks[0]);
  for (let i = 1; i < ks.length; i++) {
    const a = ks[i - 1];
    const b = ks[i];
    if (t <= b.t) {
      const p = EASES[b.ease ?? 'inOut'](clamp01((t - a.t) / Math.max(1e-3, b.t - a.t)));
      return value(a) + (value(b) - value(a)) * p;
    }
  }
  return value(ks[ks.length - 1]);
}

// ---------------------------------------------------------------------------
// Text effects, through styled runs (no reflow: hidden text stays laid out)
// ---------------------------------------------------------------------------

function mapRange(runs: TextRun[], start: number, end: number, fn: (style: TextRunStyle) => TextRunStyle): TextRun[] {
  if (end <= start) return runs;
  const out: TextRun[] = [];
  let pos = 0;
  for (const run of runs) {
    const a = pos;
    const b = pos + run.text.length;
    pos = b;
    if (b <= start || a >= end) {
      out.push(run);
      continue;
    }
    const s = Math.max(start, a) - a;
    const e = Math.min(end, b) - a;
    if (s > 0) out.push({ ...run, text: run.text.slice(0, s) });
    out.push({ text: run.text.slice(s, e), style: fn({ ...(run.style ?? {}) }) });
    if (e < run.text.length) out.push({ ...run, text: run.text.slice(e) });
  }
  return out.filter((r) => r.text.length > 0);
}

const HIDDEN: TextRunStyle = { color: 'rgba(0,0,0,0)', highlightColor: 'rgba(0,0,0,0)', textShadow: 'none', strokeWidthRatio: 0 };

/** The range of `needle` in `text`; the whole text without a needle; null when it's gone (edited away). */
function rangeOf(text: string, needle?: string): [number, number] | null {
  if (!needle) return [0, text.length];
  const i = text.toLowerCase().indexOf(needle.toLowerCase());
  return i < 0 ? null : [i, i + needle.length];
}

function parseColor(c: string): [number, number, number] | null {
  const hex = c.trim().match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (hex) {
    const x = hex[1].length === 3 ? hex[1].split('').map((d) => d + d).join('') : hex[1];
    return [0, 2, 4].map((i) => parseInt(x.slice(i, i + 2), 16)) as [number, number, number];
  }
  const rgb = c.match(/rgba?\(([^)]+)\)/i);
  if (rgb) {
    const [r, g, b] = rgb[1].split(',').map((v) => parseFloat(v));
    return [r, g, b];
  }
  return null;
}
function luminance(c: string): number {
  const rgb = parseColor(c);
  if (!rgb) return 0.5;
  const [r, g, b] = rgb.map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
function contrast(a: string, b: string): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}
function colorAt(runs: TextRun[], index: number): string | undefined {
  let pos = 0;
  for (const run of runs) {
    if (index < pos + run.text.length) return run.style?.color;
    pos += run.text.length;
  }
  return undefined;
}

const STAT_NUMBER = /\d{1,3}(?:,\d{3})+(?:\.\d+)?|\d+(?:\.\d+)?/;

function applyTextEffects(overlay: TextOverlay, el: DirectedSceneElement, t: number): TextOverlay {
  let text = overlay.text;
  for (const em of el.emphasis ?? []) {
    if (em.type !== 'countUp') continue;
    const range = rangeOf(text, em.text);
    if (!range) continue;
    const match = text.slice(range[0], range[1]).match(STAT_NUMBER);
    if (!match || match.index == null) continue;
    const grouped = match[0].includes(',');
    const target = parseFloat(match[0].replace(/,/g, ''));
    const decimals = (match[0].split('.')[1] ?? '').length;
    const p = EASES.out(clamp01((t - em.at) / (em.duration ?? 1.2)));
    const value = (em.from ?? 0) + (target - (em.from ?? 0)) * p;
    const shown = grouped
      ? value.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
      : value.toFixed(decimals);
    const at = range[0] + match.index;
    text = text.slice(0, at) + shown + text.slice(at + match[0].length);
  }
  let runs: TextRun[] = text === overlay.text ? getRenderableRuns(overlay) ?? [{ text }] : [{ text }];
  let styled = runs.some((r) => r.style && Object.keys(r.style).length > 0);
  for (const em of el.emphasis ?? []) {
    if (t < em.at || em.type === 'countUp' || em.type === 'pulse') continue;
    const range = rangeOf(text, em.text);
    if (!range) continue;
    const [s, e] = range;
    const p = clamp01((t - em.at) / (em.duration ?? 0.4));
    if (em.type === 'highlight') {
      // Highlighted words stay readable on the marker, whatever colour was asked for
      const marker = em.color ?? '#FFE066';
      const base = colorAt(runs, s) ?? overlay.style?.color ?? '#000000';
      const ink = contrast(base, marker) >= 3 ? base : contrast('#111111', marker) >= contrast('#FFFFFF', marker) ? '#111111' : '#FFFFFF';
      runs = mapRange(runs, s, e, (st) => ({ ...st, highlightColor: marker, opacity: Math.round(100 * EASES.out(p)), color: ink }));
    }
    if (em.type === 'color') runs = mapRange(runs, s, e, (st) => ({ ...st, color: em.color ?? '#FFE066' }));
    if (em.type === 'strike') runs = mapRange(runs, s, e, (st) => ({ ...st, textDecoration: 'line-through' as TextRunStyle['textDecoration'], ...(em.color ? { color: em.color } : {}) }));
    if (em.type === 'underline') runs = mapRange(runs, s, e, (st) => ({ ...st, textDecoration: 'underline' }));
    styled = true;
  }
  const rv = el.reveal;
  if (rv && (rv.type === 'words' || rv.type === 'chars') && t >= rv.at) {
    const units = rv.type === 'words' ? text.split(/(?<=\s)/) : text.split('');
    const duration = rv.duration ?? (rv.type === 'words' ? units.length * 0.16 : units.length * 0.035);
    const shownUnits = Math.floor(clamp01((t - rv.at) / Math.max(0.05, duration)) * units.length + 1e-6);
    const shownChars = units.slice(0, shownUnits).join('').length;
    if (shownChars < text.length) {
      runs = mapRange(runs, shownChars, text.length, (st) => ({ ...st, ...HIDDEN }));
      styled = true;
    }
  }
  return { ...overlay, text, runs: styled ? runs : overlay.runs };
}

// ---------------------------------------------------------------------------
// One element on stage
// ---------------------------------------------------------------------------

/** The element's box in % of the frame; text height is estimated generously (clipping a line is worse). */
function boxOf(o: AnyOverlay) {
  const x = o.position?.x ?? 0;
  const y = o.position?.y ?? 0;
  const w = o.width ?? 30;
  let h = (o as { height?: number }).height;
  if (h == null && (o.type === 'text' || !o.type)) {
    const fontSize = (o as TextOverlay).style?.fontSize ?? 40;
    const lines = Math.max(1, Math.ceil(((o as TextOverlay).text ?? '').length * fontSize * 0.62 / Math.max(1, (w / 100) * 1080)));
    h = Math.max(5, (fontSize / 1350) * 100 * 1.5 * lines);
  }
  return { x, y, w, h: h ?? 10 };
}

const STATIC_ELEMENT: DirectedSceneElement = {};

const StagedElement: React.FC<{
  overlay: AnyOverlay;
  el: DirectedSceneElement;
  t: number;
  W: number;
  H: number;
  slideData: SlideData;
  fps: number;
}> = ({ overlay, el, t, W, H, slideData, fps }) => {
  const rv = el.reveal;
  // Images stay mounted for the whole scene and hide by opacity: an <Img>
  // mounted mid-scene can miss frames that parallel render tabs capture.
  const keepMounted = overlay.type === 'image';
  let hidden = false;
  if (rv && t < rv.at) {
    if (!keepMounted) return null;
    hidden = true;
  }
  if (el.exit?.type === 'cut' && t >= el.exit.at) {
    if (!keepMounted) return null;
    hidden = true;
  }
  const box = boxOf(overlay);
  let opacity = sampleKeys(el.keys, t, 'opacity', 1);
  let dx = sampleKeys(el.keys, t, 'dx', 0);
  let dy = sampleKeys(el.keys, t, 'dy', 0);
  let scale = sampleKeys(el.keys, t, 'scale', 1);
  const rotate = sampleKeys(el.keys, t, 'rotate', 0);
  let filter: string | undefined;
  let clipPath: string | undefined;
  if (!hidden && rv && rv.type !== 'words' && rv.type !== 'chars' && rv.type !== 'cut') {
    const p = clamp01((t - rv.at) / (rv.duration ?? 0.45));
    const e = EASES.out(p);
    switch (rv.type) {
      case 'fade': opacity *= e; break;
      case 'pop': scale *= 0.55 + 0.45 * EASES.back(p); opacity *= clamp01(p * 3); break;
      case 'rise': dy += (1 - e) * 4; opacity *= e; break;
      case 'drop': dy -= (1 - e) * 4; opacity *= e; break;
      case 'slideLeft': dx += (1 - e) * 10; opacity *= e; break;
      case 'slideRight': dx -= (1 - e) * 10; opacity *= e; break;
      case 'blur': filter = `blur(${(1 - e) * 18}px)`; opacity *= e; break;
      // Clip only across the direction of travel, so a long element is never cropped
      case 'wipeRight': clipPath = `inset(0% ${Math.max(0, 100 - (box.x - 1 + (box.w + 2) * e))}% 0% 0%)`; break;
      case 'wipeDown': clipPath = `inset(0% 0% ${Math.max(0, 100 - (box.y - 1 + (box.h + 2) * e))}% 0%)`; break;
    }
  }
  for (const em of el.emphasis ?? []) {
    if (em.type !== 'pulse' || t < em.at) continue;
    const p = clamp01((t - em.at) / (em.duration ?? 0.5));
    scale *= 1 + 0.14 * Math.sin(Math.PI * p);
  }
  if (!hidden && el.exit && el.exit.type !== 'cut' && t >= el.exit.at) {
    const q = EASES.in(clamp01((t - el.exit.at) / (el.exit.duration ?? 0.35)));
    opacity *= 1 - q;
    if (el.exit.type === 'rise') dy -= 4 * q;
    if (el.exit.type === 'drop') dy += 4 * q;
    if (el.exit.type === 'shrink') scale *= 1 - 0.35 * q;
    if (el.exit.type === 'slideLeft') dx -= 10 * q;
    if (el.exit.type === 'slideRight') dx += 10 * q;
  }
  if (hidden) opacity = 0;
  if (opacity <= 0.001 && !keepMounted) return null;
  const staged = overlay.type === 'text' || !overlay.type ? applyTextEffects(overlay as TextOverlay, el, t) : overlay;
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: el.z ?? overlay.zIndex ?? 0,
        opacity,
        transform: `translate(${(dx / 100) * W}px, ${(dy / 100) * H}px) rotate(${rotate}deg) scale(${scale})`,
        transformOrigin: `${box.x + box.w / 2}% ${box.y + box.h / 2}%`,
        filter,
        clipPath,
      }}
    >
      <AnimatedOverlayWrapper
        overlay={staged}
        entrance={{ overlayId: overlay.id, type: 'none', delaySeconds: 0, durationSeconds: 0 }}
        containerWidth={W}
        containerHeight={H}
        fps={fps}
        referenceWidth={slideData.dimensions.width}
        referenceHeight={slideData.dimensions.height}
        layoutEngine="remotion"
        imageUrls={slideData.imageUrls}
        adaptTextToHeight
        sourceTextLayout={{ width: slideData.dimensions.width, height: slideData.dimensions.height, referenceWidth: slideData.dimensions.width }}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Slides on stage
// ---------------------------------------------------------------------------

/** The plan watermark (server and editor ids) is not slide content: it never moves with the camera or transitions. */
export const isWatermark = (o: AnyOverlay) =>
  o.id === 'wavegen-watermark' || o.id === '__watermark__' || (o.type === 'image' && (o as { filePath?: string }).filePath === 'wavegen-watermark.png');

/** A slide's overlays in the 9:16 frame, as the original carousel video lays them out (watermark excluded). */
export function adaptedOverlays(sd: SlideData, W: number, H: number): AnyOverlay[] {
  const sw = sd.dimensions.width;
  const sh = sd.dimensions.height;
  return [...sd.overlays]
    .filter((o) => !isWatermark(o))
    .sort((a, b) => (a.zIndex ?? 0) - (b.zIndex ?? 0))
    .map((o) => adaptVideoOverlay(o, sw, sh, W, H, sw, sd.overlays, 'remotion'));
}

/** The watermark as it sits on the first slide that carries one, laid out in the 9:16 frame. */
function watermarkOf(slides: SlideData[], W: number, H: number): { sd: SlideData; overlay: AnyOverlay } | null {
  for (const sd of slides) {
    const o = sd.overlays.find(isWatermark);
    if (o) return { sd, overlay: adaptVideoOverlay(o, sd.dimensions.width, sd.dimensions.height, W, H, sd.dimensions.width, sd.overlays, 'remotion') };
  }
  return null;
}

const SlideBackground: React.FC<{ sd: SlideData; W: number; H: number; fps: number }> = ({ sd, W, H, fps }) => {
  const sw = sd.dimensions.width;
  const sh = sd.dimensions.height;
  const cover = Math.max(W / sw, H / sh);
  const bw = sw * cover;
  const bh = sh * cover;
  return (
    <div style={{ position: 'absolute', inset: 0, backgroundColor: sd.backgroundColor ?? '#000', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', left: (W - bw) / 2, top: (H - bh) / 2, width: bw, height: bh }}>
        <RemotionSlide
          width={bw}
          height={bh}
          backgroundColor={sd.backgroundColor}
          backgroundImageUrl={sd.backgroundImageUrl}
          backgroundFilter={sd.backgroundFilter}
          overlays={[]}
          imageUrls={sd.imageUrls}
          isVideo={false}
          fps={fps}
          referenceWidth={sw}
          referenceHeight={sh}
          layoutEngine="remotion"
        />
      </div>
    </div>
  );
};

function isPlate(o: AnyOverlay): boolean {
  if (o.type === 'text' || !o.type) return false;
  const w = o.width ?? 0;
  const h = (o as { height?: number }).height ?? 0;
  return (w >= 90 && h >= 60) || (w >= 95 && h <= 2);
}

const SceneView: React.FC<{
  scene: DirectedScene;
  slides: SlideData[];
  W: number;
  H: number;
  pinned: Set<string>;
  hasBackdrop: boolean;
}> = ({ scene, slides, W, H, pinned, hasBackdrop }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const t = frame / fps;
  const vertical = scene.stack !== 'horizontal';
  const cells = scene.slides.length ? scene.slides : [0];
  const cellOffset = (i: number) => (vertical ? { left: 0, top: i * H } : { left: i * W, top: 0 });
  const listed = new Set(scene.elements.filter((e) => e.ref).map((e) => refKey(e.ref!)));
  const hidden = new Set(scene.hide ?? []);
  const camX = sampleKeys(scene.camera, t, 'x', 0.5);
  const camY = sampleKeys(scene.camera, t, 'y', 0.5);
  const zoom = sampleKeys(scene.camera, t, 'zoom', 1);
  const camera = `translate(${W / 2 - camX * W * zoom}px, ${H / 2 - camY * H * zoom}px) scale(${zoom})`;
  // Only the stacked slides the camera can see are drawn: several full-bleed
  // photo slides at once starve headless Chrome into dropping images.
  const along = vertical ? camY : camX;
  const reach = 0.5 / zoom + 0.15;
  const cellVisible = (i: number) => i + 1 > along - reach && i < along + reach;
  const showBackground = scene.background ?? !hasBackdrop;
  return (
    <AbsoluteFill style={{ backgroundColor: showBackground ? slides[cells[0]]?.backgroundColor ?? '#000' : 'transparent', overflow: 'hidden' }}>
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: vertical ? W : W * cells.length,
          height: vertical ? H * cells.length : H,
          transform: camera,
          transformOrigin: '0 0',
        }}
      >
        {cells.map((slideIndex, i) => {
          const sd = slides[slideIndex];
          if (!sd || !cellVisible(i)) return null;
          return (
            <div key={`cell-${i}`} style={{ position: 'absolute', ...cellOffset(i), width: W, height: H }}>
              {showBackground && <SlideBackground sd={sd} W={W} H={H} fps={fps} />}
              {adaptedOverlays(sd, W, H)
                .filter((o) => {
                  const k = `${slideIndex}:${o.id}`;
                  return !listed.has(k) && !hidden.has(k) && !hidden.has(o.id) && !pinned.has(k) && !(hasBackdrop && !showBackground && isPlate(o));
                })
                .map((o) => (
                  <StagedElement key={o.id} overlay={o} el={STATIC_ELEMENT} t={t} W={W} H={H} slideData={sd} fps={fps} />
                ))}
            </div>
          );
        })}
        {/* Staged elements keep the design's stacking order: animating something never lifts it over what sits on top of it */}
        {scene.elements.map((el, i) => {
          const source = el.ref ?? el.add?.styleFrom;
          if (!source) return null;
          const sd = slides[source.slide];
          const base = sd && adaptedOverlays(sd, W, H).find((o) => o.id === source.overlay);
          if (!sd || !base) return null;
          let overlay: AnyOverlay = base;
          if (el.add) {
            const style = (base as TextOverlay).style;
            overlay = {
              ...(base as TextOverlay),
              id: `add-${i}`,
              type: 'text',
              text: el.add.text,
              runs: undefined,
              position: { x: el.add.x, y: el.add.y },
              width: el.add.width,
              height: undefined,
              zIndex: 900 + i,
              style: {
                ...style,
                fontSize: (style?.fontSize ?? 40) * (el.add.fontScale ?? 1),
                textAlign: el.add.align ?? style?.textAlign ?? 'left',
                ...(el.add.color ? { color: el.add.color } : {}),
              },
            } as TextOverlay;
          }
          const cell = el.cell ?? Math.max(0, cells.indexOf(source.slide));
          if (!cellVisible(cell)) return null;
          return (
            <div key={`el-${i}`} style={{ position: 'absolute', ...cellOffset(cell), width: W, height: H, zIndex: el.add ? el.z ?? overlay.zIndex ?? 0 : overlay.zIndex ?? 0 }}>
              <StagedElement overlay={overlay} el={el} t={t} W={W} H={H} slideData={sd} fps={fps} />
            </div>
          );
        })}
      </div>
    </AbsoluteFill>
  );
};

function presentation(transition: DirectedSceneTransition): any {
  switch (transition.type) {
    case 'slideLeft': return slide({ direction: 'from-right' });
    case 'slideUp': return slide({ direction: 'from-bottom' });
    case 'wipeLeft': return wipe({ direction: 'from-right' });
    case 'wipeUp': return wipe({ direction: 'from-bottom' });
    default: return fade();
  }
}

const hasTransition = (scene: DirectedScene) => !!scene.transitionOut && scene.transitionOut.type !== 'cut' && scene.transitionOut.durationSeconds > 0;

export function calculateDirectedVideoDuration(spec: DirectedSpec): number {
  const fps = spec.fps || 30;
  let frames = 0;
  spec.scenes.forEach((scene, i) => {
    frames += Math.round(scene.durationSeconds * fps);
    if (i < spec.scenes.length - 1 && hasTransition(scene)) frames -= Math.round(scene.transitionOut!.durationSeconds * fps);
  });
  return Math.max(1, frames);
}

export const DirectedVideo: React.FC<DirectedVideoProps> = ({ slides, spec, width, height, musicUrl, musicVolume = 0.7 }) => {
  const { fps, durationInFrames } = useVideoConfig();
  const frame = useCurrentFrame();
  const pinned = new Set((spec.persistent ?? []).map(refKey));
  const backdrop = spec.backdrop ? slides[spec.backdrop.slide] : undefined;
  const watermark = watermarkOf(slides, width, height);
  const volume = React.useCallback(
    (f: number) =>
      interpolate(f, [0, Math.round(1.5 * fps)], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }) *
      interpolate(f, [durationInFrames - 2 * fps, durationInFrames], [1, 0], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }) *
      musicVolume,
    [fps, durationInFrames, musicVolume]
  );
  const images = Array.from(
    new Set(slides.flatMap((sd) => [sd.backgroundImageUrl, ...Object.values(sd.imageUrls ?? {})]).filter((u): u is string => !!u))
  );
  return (
    <>
      {musicUrl && <Audio src={musicUrl} volume={volume} loop />}
      <>
        {/* Every image, decoded before the first frame and kept alive: images
            mounted mid-video otherwise race the capture in parallel render
            tabs and drop out of single frames. */}
        <AbsoluteFill style={{ opacity: 0, pointerEvents: 'none' }}>
          {images.map((src) => (
            <Img key={src} src={src} style={{ position: 'absolute', width: 1, height: 1 }} />
          ))}
        </AbsoluteFill>
        <AbsoluteFill style={{ backgroundColor: backdrop?.backgroundColor ?? slides[0]?.backgroundColor ?? '#000' }}>
          {/* Layers: backdrop, then scenes, then pinned chrome, then the watermark */}
          {backdrop && (
            <AbsoluteFill style={{ zIndex: 0 }}>
              <SlideBackground sd={backdrop} W={width} H={height} fps={fps} />
              {adaptedOverlays(backdrop, width, height)
                .filter(isPlate)
                .map((o) => (
                  <StagedElement key={o.id} overlay={o} el={STATIC_ELEMENT} t={0} W={width} H={height} slideData={backdrop} fps={fps} />
                ))}
            </AbsoluteFill>
          )}
          <AbsoluteFill style={{ zIndex: 1 }}>
            <TransitionSeries>
              {spec.scenes.map((scene, i) => (
                <React.Fragment key={i}>
                  <TransitionSeries.Sequence durationInFrames={Math.max(1, Math.round(scene.durationSeconds * fps))}>
                    <SceneView scene={scene} slides={slides} W={width} H={height} pinned={pinned} hasBackdrop={!!backdrop} />
                  </TransitionSeries.Sequence>
                  {i < spec.scenes.length - 1 && hasTransition(scene) && (
                    <TransitionSeries.Transition
                      presentation={presentation(scene.transitionOut!)}
                      timing={
                        scene.transitionOut!.type === 'fade'
                          ? linearTiming({ durationInFrames: Math.round(scene.transitionOut!.durationSeconds * fps) })
                          : springTiming({ config: { damping: 200 }, durationInFrames: Math.round(scene.transitionOut!.durationSeconds * fps) })
                      }
                    />
                  )}
                </React.Fragment>
              ))}
            </TransitionSeries>
          </AbsoluteFill>
          <AbsoluteFill style={{ zIndex: 2 }}>
            {(spec.persistent ?? []).map((r) => {
              const sd = slides[r.slide];
              const o = sd && adaptedOverlays(sd, width, height).find((x) => x.id === r.overlay);
              return o ? <StagedElement key={refKey(r)} overlay={o} el={STATIC_ELEMENT} t={frame / fps} W={width} H={height} slideData={sd} fps={fps} /> : null;
            })}
          </AbsoluteFill>
          {watermark && (
            <AbsoluteFill style={{ zIndex: 3 }}>
              <StagedElement overlay={watermark.overlay} el={STATIC_ELEMENT} t={0} W={width} H={height} slideData={watermark.sd} fps={fps} />
            </AbsoluteFill>
          )}
        </AbsoluteFill>
      </>
    </>
  );
};
